import { Component } from '@theme/component';

/** Isolated design controls: this component never submits forms or calls commerce APIs. */
class ReferencePreview extends Component {
  #motionQuery;
  #stickyObserver;
  #observeSticky() {
    this.#stickyObserver?.disconnect();
    const sticky = this.querySelector('[data-sticky-cta]');
    if (!sticky) return;
    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.boundingClientRect.bottom > 72;
    }, { rootMargin: '-72px 0px 0px', threshold: 0 });
    this.#stickyObserver.observe(this);
  }
  #handleMotion = () => {
    for (const video of this.querySelectorAll('video')) {
      if (this.#motionQuery?.matches) video.pause();
      else video.play().catch(() => {});
    }
  };

  #centerDifferenceTestimonials() {
    if (!document.body.classList.contains('difference-preview') || !this.classList.contains('ref-region--testimonials')) return;
    const track = this.querySelector('[data-track]');
    if (!(track instanceof HTMLElement) || track.dataset.centered) return;
    const card = track.querySelectorAll('.ref-card')[1];
    if (!(card instanceof HTMLElement)) return;
    track.scrollLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
    track.dataset.centered = 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', this.#handleMotion);
    this.#handleMotion();
    this.#observeSticky();
    this.#centerDifferenceTestimonials();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#handleMotion();
    this.#observeSticky();
    this.#centerDifferenceTestimonials();
  }

  disconnectedCallback() {
    this.#stickyObserver?.disconnect();
    this.#motionQuery?.removeEventListener('change', this.#handleMotion);
    super.disconnectedCallback();
  }
  /** @param {MouseEvent} event */
  selectMedia(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-media-index]') : null;
    if (!button) return;
    const index = button.getAttribute('data-media-index');
    for (const panel of this.querySelectorAll('[data-media-panel]')) {
      panel.hidden = panel.getAttribute('data-media-panel') !== index;
    }
    for (const control of this.querySelectorAll('[data-media-index]')) {
      control.setAttribute('aria-pressed', String(control === button));
    }
  }

  /** @param {MouseEvent} event */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab) return;
    const group = tab.getAttribute('data-group');
    for (const control of this.querySelectorAll('[role="tab"]')) {
      control.setAttribute('aria-selected', String(control === tab));
      control.setAttribute('tabindex', control === tab ? '0' : '-1');
    }
    for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
      panel.hidden = panel.getAttribute('data-group') !== group;
    }
  }

  /** @param {KeyboardEvent} event */
  tabKey(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const current = tabs.indexOf(event.target);
    if (current < 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
      (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[next]?.click();
    tabs[next]?.focus();
  }

  /** @param {MouseEvent} event */
  selectTimeline(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-timeline]') : null;
    if (!button) return;
    for (const card of this.querySelectorAll('[data-timeline]')) {
      const selected = card === button;
      card.setAttribute('aria-pressed', String(selected));
      const text = card.querySelector('[data-timeline-text]');
      if (text) text.hidden = !selected;
    }
  }

  /** @param {MouseEvent} event */
  scrollCards(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-direction]') : null;
    const track = this.querySelector('[role="tabpanel"]:not([hidden]) [data-track]') || this.querySelector('[data-track]');
    if (!button || !(track instanceof HTMLElement)) return;
    const direction = Number(button.getAttribute('data-direction'));
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.body.classList.contains('difference-preview') && this.classList.contains('ref-region--testimonials')) {
      const card = track.querySelector('.ref-card');
      if (!(card instanceof HTMLElement)) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card.clientWidth + gap), behavior: reduced ? 'instant' : 'smooth' });
      return;
    }
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: reduced ? 'instant' : 'smooth' });
  }

  /** No payment, subscription, review, email, or customer data leaves the browser. */
  simulate() {
    const status = this.querySelector('[data-preview-status]');
    if (status instanceof HTMLElement) status.hidden = false;
  }

  /** Sticky CTA remains a local simulation and never submits an order. */
  previewPurchase() {
    const status = this.querySelector('[data-sticky-status]');
    if (status instanceof HTMLElement) status.hidden = false;
  }

  /** Opens only the already-selected gallery image in a native modal. */
  zoom() {
    const dialog = this.querySelector('dialog');
    const source = this.querySelector('[data-media-panel]:not([hidden]) img');
    const image = dialog?.querySelector('img');
    if (!(dialog instanceof HTMLDialogElement) || !(source instanceof HTMLImageElement) || !image) return;
    image.src = source.currentSrc || source.src;
    image.alt = source.alt;
    dialog.showModal();
  }

  closeZoom() {
    const dialog = this.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) dialog.close();
  }

  showReviews() {
    for (const review of this.querySelectorAll('[data-extra-review]')) review.hidden = false;
    const button = this.querySelector('[data-show-reviews]');
    if (button instanceof HTMLElement) button.hidden = true;
  }

  /** @param {Event} event */
  sortReviews(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const container = this.querySelector('.ref-reviews');
    if (!container) return;
    const cards = [...container.querySelectorAll('[data-review-index]')];
    const ratingOrder = event.target.value === 'rating';
    cards.sort((a, b) => ratingOrder ? Number(b.getAttribute('data-rating')) - Number(a.getAttribute('data-rating')) :
      Number(a.getAttribute('data-review-index')) - Number(b.getAttribute('data-review-index')));
    for (const card of cards) container.append(card);
  }
}

if (!customElements.get('reference-preview')) customElements.define('reference-preview', ReferencePreview);

